// Record actions apps declare (ActionDef): buttons in the card, a small form when the action needs input, the server's reason on refusal.

import { Fragment, useRef, useState } from 'react';

const MAIN = 2;
import { bindMe, evaluate, hasRight, recordTitle, resolveSetting, settingChain, type ActionDef, type ActionField, type ActionReport, type MetaIndex, type RecordObj } from '@confdeck/core-shared';
import { navigate } from '../shell/router.ts';
import { PickList } from '../ui/PickList.tsx';
import { useAccess, useMeta, useSync } from '../sync/react.tsx';
import { Icon } from '../ui/icons.tsx';
import { BlockEditor } from '../editor/BlockEditor.tsx';
import { NOTE_KINDS } from '../editor/kinds.ts';
import { Button, Checkbox, Chevron, Chip, Dialog, Input, Menu, MenuContent, MenuItem, MenuSeparator, MenuTrigger, useToast, Field } from '../ui/primitives.tsx';
import { Segmented } from '../ui/controls.tsx';
import { Select } from '../ui/menus.tsx';
import { choiceControl, commandRequest, mayClose, txKeyFor } from './action-form.ts';
import { DateInput } from '../ui/DateInput.tsx';
import s from './record.module.css';

export function useActions(rec: RecordObj | undefined): ActionDef[] {
  const client = useSync();
  const access = useAccess();
  if (!rec) return [];
  // An action whose right the person lacks (platform П6) is not offered; the server refuses it anyway.
  return (client.header?.actions ?? []).filter((a) => a.typeId === rec.typeId && hasRight(access, a.right) && (!a.when || evaluate(client.store, rec, bindMe(a.when, client.header?.userId)!)));
}

export function RecordActions({ rec }: { rec: RecordObj }) {
  const client = useSync();
  const toast = useToast();
  const actions = useActions(rec);
  const [open, setOpen] = useState<ActionDef | null>(null);
  if (!actions.length) return null;
  const go = async (a: ActionDef) => {
    if (a.href) {
      // `{data.key}` takes a value of the record (a response points at the record it created); an in-app path opens here, a site link in a new tab.
      const url = a.href.replace('{site}', client.header?.siteUrl ?? '').replace('{id}', rec.id).replace(/\{data\.([a-z0-9_]+)\}/g, (_, k: string) => encodeURI(String(rec.data[k] ?? '')));
      if (url.startsWith('/')) navigate(url); else window.open(url, '_blank');
      return;
    }
    if (a.form?.length || a.confirm) { setOpen(a); return; }
    const r = await client.run(a.command, { id: rec.id, ...(a.input ?? {}) });
    toast(r.ok ? { kind: 'info', text: `Готово: ${a.label.toLowerCase()}` } : { kind: 'error', text: r.error.message });
  };
  // Two main actions stay buttons; the rest, dangerous ones last, go under «Ещё» (audit 28.10, L5): the card leads to one step.
  const safe = actions.filter((a) => !a.danger);
  const main = safe.slice(0, MAIN);
  const more = [...safe.slice(MAIN), ...actions.filter((a) => a.danger)];
  return (
    <div className={s.actions} data-actions>
      {main.map((a) => <Button key={a.key} size="sm" variant="secondary" icon={a.icon} onClick={() => void go(a)}>{a.label}</Button>)}
      {more.length > 0 && (
        <Menu>
          <MenuTrigger asChild><Button size="sm" icon="more">{main.length ? 'Ещё' : 'Действия'}</Button></MenuTrigger>
          <MenuContent>{more.map((a, i) => <Fragment key={a.key}>{a.danger && i > 0 && !more[i - 1]!.danger && <MenuSeparator />}<MenuItem icon={a.icon} danger={a.danger} onSelect={() => void go(a)}>{a.label}</MenuItem></Fragment>)}</MenuContent>
        </Menu>
      )}
      {open && <ActionDialog action={open} rec={rec} onClose={() => setOpen(null)} />}
    </div>
  );
}

function ActionDialog({ action, rec, onClose }: { action: ActionDef; rec: RecordObj; onClose: () => void }) {
  const client = useSync();
  const meta = useMeta();
  const toast = useToast();
  const [values, setValues] = useState<Record<string, unknown>>(() => {
    const init: Record<string, unknown> = {};
    for (const f of action.form ?? []) {
      const own = f.valueSetting ? settingValue(client, meta, f.valueSetting, rec) : undefined;
      if (own !== undefined && (f.kind !== 'term' || meta.term(String(own)))) init[f.key] = own;
      else if (f.value !== undefined) init[f.key] = f.value;
      // A checkbox always has an answer: unchecked means «нет».
      else if (f.kind === 'checkbox') init[f.key] = false;
    }
    return init;
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState<ActionReport | null>(null);
  const tx = useRef<{ key: string; sig: string } | null>(null);
  const close = () => { if (mayClose(busy)) onClose(); };
  // A field asked only for some answers of another one (a period asks for its end): a hidden field is neither checked nor sent.
  const fields = (action.form ?? []).filter((f) => shown(meta, f, values));
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    for (const f of fields) if (f.required && (values[f.key] === undefined || values[f.key] === '')) { setError(`Заполните «${f.label}»`); return; }
    setBusy(true);
    const input: Record<string, unknown> = { id: rec.id };
    for (const f of fields) if (values[f.key] !== undefined && values[f.key] !== '') input[f.key] = f.kind === 'number' ? Number(values[f.key]) : values[f.key];
    if (action.open || action.report) {
      // The answer names the record to open (or is the report to show), so the command goes over HTTP; the socket only says it was applied.
      // The key of the send is the socket's `clientTxId`: the same answers sent again replay the first result instead of running a second time.
      const sent = { ...input, ...(action.input ?? {}) };
      tx.current = txKeyFor(tx.current, sent);
      const res = await fetch(`/api/commands/${action.command}`, commandRequest(sent, tx.current.key)).catch(() => null);
      const body = await res?.json().catch(() => ({})) as { result?: Record<string, unknown>; error?: { message: string } } | undefined;
      setBusy(false);
      if (!res || !body) { setError('Нет связи с сервером. Повторите: уже созданное второй раз не появится'); return; }
      if (!res.ok) { setError(body.error?.message ?? 'Не получилось'); return; }
      if (action.report && body.result?.report) { setReport(body.result.report as ActionReport); return; }
      toast({ kind: 'info', text: `Готово: ${action.label.toLowerCase()}` });
      onClose();
      const target = action.open ? body.result?.[action.open] : undefined;
      if (typeof target === 'string') await openWhenSynced(client, target);
      return;
    }
    const r = await client.run(action.command, { ...input, ...(action.input ?? {}) });
    setBusy(false);
    if (r.ok) { toast({ kind: 'info', text: `Готово: ${action.label.toLowerCase()}` }); onClose(); }
    else setError(r.error.message);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && close()} label={action.label}>
      {report ? <ReportView action={action} report={report} onClose={onClose} /> : (
        <form className={s.dialog} onSubmit={(e) => void submit(e)}>
          <h2 className={s.dialogTitle}><Icon name={action.icon} size={16} />{action.label}</h2>
          {action.confirm && <p className={s.dialogText}>{action.confirm}</p>}
          {fields.map((f) => (
            <Field key={f.key} as={f.kind === 'textarea' || f.kind === 'choice' ? 'div' : undefined} label={<>{f.label}{f.required ? ' *' : ''}</>} hint={f.hint}>
              <FormField f={f} rec={rec} value={values[f.key]} onChange={(v) => { setValues({ ...values, [f.key]: v }); setError(null); }} />
            </Field>
          ))}
          {error && <div className={s.dialogError} role="alert">{error}</div>}
          <div className={s.dialogFoot}>
            <Button onClick={close} disabled={busy}>Отмена</Button>
            <Button type="submit" variant={action.danger ? 'secondary' : 'primary'} disabled={busy}>{busy && action.report ? 'Выполняется…' : action.label}</Button>
          </div>
        </form>
      )}
    </Dialog>
  );
}

/** The value of a setting for a record: its own, else its parent's (TypeDef.settingsParent), else the account's or the default. */
function settingValue(client: ReturnType<typeof useSync>, meta: MetaIndex, key: string, rec: RecordObj): unknown {
  const d = client.header?.settings?.find((x) => x.key === key);
  if (!d) return undefined;
  const parentOf = (id: string) => {
    const r = client.store.record(id);
    const pf = r ? meta.type(r.typeId)?.settingsParent?.field : undefined;
    const f = r && pf ? meta.field(r.typeId, pf) : undefined;
    return r && f ? client.store.linked(r, f)[0] : null;
  };
  return resolveSetting(meta, d, meta.user(client.header?.userId), null, settingChain(rec.id, parentOf)).value;
}

/** Whether a field of the form is asked with these answers (ActionField.shownIf): a term counts with its parents. */
function shown(meta: MetaIndex, f: ActionField, values: Record<string, unknown>): boolean {
  if (!f.shownIf) return true;
  const v = values[f.shownIf.key];
  let hit = false;
  for (let t = typeof v === 'string' && v ? meta.term(v) : undefined, i = 0; !hit && i < 6; i++) {
    if (!t) break;
    hit = f.shownIf.values.includes(t.id);
    t = t.parentId ? meta.term(t.parentId) : undefined;
  }
  if (!hit && typeof v === 'string') hit = f.shownIf.values.includes(v);
  return f.shownIf.not ? !hit : hit;
}

/** The report of a batch (ActionDef.report): the counts, then one line per item — a link to what it made, the reason of a refusal. */
function ReportView({ action, report, onClose }: { action: ActionDef; report: ActionReport; onClose: () => void }) {
  return (
    <div className={s.dialog} data-action-report>
      <h2 className={s.dialogTitle}><Icon name={action.icon} size={16} />{action.label}: итог</h2>
      <p className={s.dialogText}>
        Создано: {report.done}{report.skipped ? ` · уже были: ${report.skipped}` : ''}{report.failed ? ` · не получилось: ${report.failed}` : ''} — из {report.total}.
        {report.failed ? ' Остальные созданы; повторите действие после исправления — созданные второй раз не появятся.' : ''}
      </p>
      <ul className={s.reportList}>
        {report.lines.map((l, i) => (
          <li key={i} className={l.ok ? undefined : s.reportFailed}>
            <Icon name={l.ok ? (l.note ? 'eye' : 'tick') : 'close'} size={14} />
            {l.id ? <a href={`/r/${l.id}`} onClick={(e) => { e.preventDefault(); onClose(); navigate(`/r/${l.id}`); }}>{l.label}</a> : <span>{l.label}</span>}
            {l.note && <span className={s.reportNote}>{l.note}</span>}
          </li>
        ))}
      </ul>
      <div className={s.dialogFoot}><Button variant="primary" onClick={onClose}>Готово</Button></div>
    </div>
  );
}

/** Opens a record once the sync has brought it (a few seconds at most), so the card does not show «Запись удалена». */
async function openWhenSynced(client: ReturnType<typeof useSync>, id: string): Promise<void> {
  for (let i = 0; i < 25 && !client.store.record(id); i++) await new Promise((r) => setTimeout(r, 200));
  navigate(`/r/${id}`);
}

function FormField({ f, rec, value, onChange }: { f: ActionField; rec: RecordObj; value: unknown; onChange: (v: unknown) => void }) {
  const client = useSync();
  const meta = useMeta();
  const [picking, setPicking] = useState(false);
  if (f.kind === 'checkbox') return <Checkbox label={f.label} checked={value === true} onChange={onChange} />;
  // A comment of an action is a long text for people: the block editor, as on the card (decision 034).
  if (f.kind === 'textarea') return <BlockEditor compact value={String(value ?? '')} label={f.label} kinds={NOTE_KINDS} onChange={(md) => onChange(md)} />;
  if (f.kind === 'record' || f.kind === 'term') {
    const options = f.kind === 'record' ? recordOptions(client, meta, f, rec) : termOptions(meta, f);
    const chosen = options.find((o) => o.id === value);
    return picking ? (
      <div className={s.dialogPick}><PickList options={options} selected={value ? [String(value)] : []} onPick={(v) => onChange(v)} onDone={() => setPicking(false)} /></div>
    ) : (
      <button type="button" className={s.dialogSelect} onClick={() => setPicking(true)}>{chosen ? chosen.render ?? chosen.label : <span className={s.empty}>Выбрать…</span>}<Chevron size={13} /></button>
    );
  }
  if (f.kind === 'date') return <DateInput value={String(value ?? '')} onChange={onChange} />;
  if (f.kind === 'choice') {
    const opts = f.options ?? [];
    const control = choiceControl(opts.length);
    // DESIGN.md: more than five options do not fit a row of segments (a phone) — a menu, with a search above eight.
    if (control !== 'segmented') return <Select label={f.label} value={typeof value === 'string' ? value : ''} options={opts} search={control === 'select-search'} onChange={onChange} />;
    return <Segmented label={f.label} value={typeof value === 'string' ? value : null} items={opts.map((o) => ({ value: o.value, label: o.label }))} onChange={onChange} />;
  }
  if (f.kind === 'time') return <Input type="time" value={String(value ?? '')} onChange={(e) => onChange(e.target.value)} />;
  return <Input type={f.kind === 'number' ? 'number' : f.kind === 'email' ? 'email' : 'text'} value={String(value ?? '')} placeholder={f.placeholder} onChange={(e) => onChange(e.target.value)} />;
}

function recordOptions(client: ReturnType<typeof useSync>, meta: ReturnType<typeof useMeta>, f: ActionField, rec: RecordObj) {
  const type = meta.types().find((t) => t.key === f.typeKey);
  if (!type) return [];
  const linkField = f.sameAs ? meta.field(type.id, f.sameAs.field) : undefined;
  // «sameAs.recordField»: «id» — linked to this record; a relation field of this record — linked to what it links to (J4: the slot's event).
  const own = f.sameAs && f.sameAs.recordField !== 'id' ? meta.field(rec.typeId, f.sameAs.recordField) : undefined;
  const targets = own ? client.store.linked(rec, own) : [rec.id];
  const out: { id: string; label: string; render?: React.ReactNode }[] = [];
  for (const rid of client.store.recordIds(type.id)) {
    const r = client.store.record(rid);
    if (!r || r.archivedAt) continue;
    if (linkField && !client.store.linked(r, linkField).some((x) => targets.includes(x))) continue;
    out.push({ id: r.id, label: recordTitle(r, meta) });
  }
  return out;
}

function termOptions(meta: ReturnType<typeof useMeta>, f: ActionField) {
  const tax = meta.taxonomies().find((t) => t.key === f.taxonomy);
  return tax ? meta.terms(tax.id).filter((t) => !f.terms || f.terms.includes(t.id)).map((t) => ({ id: t.id, label: t.name, render: <Chip color={t.color}>{t.name}</Chip> })) : [];
}
